import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import { productionAPI, pricingAPI } from '@/lib/api';

const slugCode = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);

const EMPTY = {
    label: '', code: '', point_de_vente: '', bl_start: 1, has_bl: true, bl_with_prices: false,
    pricingChoice: '', newPricingName: '', tarifMode: 'direct', commissionPct: '',
    copy_products_from: '',
};

export default function ManageClientsDialog({ open, onOpenChange, onChanged }) {
    const [clients, setClients] = useState([]);
    const [pricingClients, setPricingClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null); // {mode:'create'} ou {mode:'edit', client}
    const [saving, setSaving] = useState(false);
    const [archiveTarget, setArchiveTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        const [c, p] = await Promise.all([productionAPI.clientsAll(), pricingAPI.getClients()]);
        setClients(c.data);
        setPricingClients(p.data);
        setLoading(false);
    };
    useEffect(() => { if (open) { setForm(null); load(); } }, [open]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const openCreate = () => setForm({ mode: 'create', ...EMPTY });
    const openEdit = (c) => setForm({
        mode: 'edit', id: c.id, code: c.code, label: c.label,
        point_de_vente: c.point_de_vente || '', bl_start: c.bl_start || 1,
        has_bl: c.has_bl !== false,
        bl_with_prices: !!c.bl_with_prices,
        pricingChoice: c.pricing_client_id || '',
        pricingSaleType: c.pricing_client?.sale_type,
        tarifMode: c.pricing_client?.commission ? 'commission' : 'direct',
        commissionPct: c.pricing_client?.commission ? Math.round(c.pricing_client.commission * 1000) / 10 : '',
        hasBl: !!c.last_bl,
    });

    const submit = async () => {
        setSaving(true);
        try {
            if (form.mode === 'create') {
                const payload = {
                    label: form.label, code: form.code, point_de_vente: form.point_de_vente,
                    bl_start: parseInt(form.bl_start, 10) || 1,
                    has_bl: !!form.has_bl,
                    bl_with_prices: !!form.bl_with_prices,
                    copy_products_from: form.copy_products_from || null,
                };
                if (form.pricingChoice === '__new__') {
                    payload.new_pricing = {
                        name: form.newPricingName,
                        commission: form.tarifMode === 'commission' && form.commissionPct ? parseFloat(form.commissionPct) / 100 : null,
                    };
                } else {
                    payload.pricing_client_id = form.pricingChoice || null;
                }
                await productionAPI.createClient(payload);
                toast.success(`Client « ${form.label} » créé`);
            } else {
                await productionAPI.updateClient(form.id, {
                    label: form.label, point_de_vente: form.point_de_vente,
                    pricing_client_id: form.pricingChoice || '',
                    bl_start: parseInt(form.bl_start, 10) || 1,
                    has_bl: !!form.has_bl,
                    bl_with_prices: !!form.bl_with_prices,
                    pricing_mode: form.tarifMode,
                    pricing_commission: form.tarifMode === 'commission' && form.commissionPct ? parseFloat(form.commissionPct) / 100 : null,
                });
                toast.success('Client mis à jour');
            }
            setForm(null);
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c, active) => {
        try {
            await productionAPI.setClientActive(c.id, active);
            toast.success(active ? `${c.label} réactivé` : `${c.label} archivé — historique conservé`);
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const modeLabel = (pc) => {
        if (!pc) return '—';
        if (pc.sale_type === 'carrefour') return 'Commission 15 % + TVA (Carrefour)';
        if (pc.commission) return `Commission ${Math.round(pc.commission * 1000) / 10} %`;
        return 'Tarifs fixes';
    };

    const inputCls = 'h-8 text-sm';
    const selCls = 'w-full h-8 px-2 border rounded-md text-sm bg-white';

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-clients-dialog">
                    <DialogHeader>
                        <DialogTitle>Gérer les clients de production</DialogTitle>
                    </DialogHeader>
                    {loading ? (
                        <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                    ) : form ? (
                        <div className="space-y-3" data-testid="client-form">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Libellé *</Label>
                                    <Input className={inputCls} value={form.label}
                                        onChange={e => {
                                            const v = e.target.value;
                                            setForm(f => ({ ...f, label: v, ...(f.mode === 'create' && slugCode(f.label) === f.code ? { code: slugCode(v) } : {}) }));
                                        }}
                                        placeholder="ex. Leader Price" data-testid="client-label-input" />
                                </div>
                                <div>
                                    <Label className="text-xs">Code court {form.mode === 'edit' && <span className="text-slate-400">(non modifiable)</span>}</Label>
                                    <Input className={`${inputCls} font-mono uppercase`} value={form.code}
                                        disabled={form.mode === 'edit'}
                                        onChange={e => set('code', slugCode(e.target.value))}
                                        data-testid="client-code-input" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Point de vente (sur les BL)</Label>
                                <Input className={inputCls} value={form.point_de_vente}
                                    onChange={e => set('point_de_vente', e.target.value)}
                                    placeholder="ex. LEADER PRICE GENIPA" data-testid="client-pdv-input" />
                            </div>
                            <div>
                                <Label className="text-xs">Client tarifaire (Tarifs & marges, Comparatif)</Label>
                                <select className={selCls} value={form.pricingChoice}
                                    onChange={e => set('pricingChoice', e.target.value)}
                                    data-testid="client-pricing-select">
                                    <option value="">— Aucun —</option>
                                    {pricingClients.map(pc => (
                                        <option key={pc.id} value={pc.id}>
                                            {pc.name}{pc.sale_type === 'carrefour' ? ' (Carrefour 15 % + TVA)' : pc.commission ? ` (commission ${Math.round(pc.commission * 1000) / 10} %)` : ' (tarifs fixes)'}
                                        </option>
                                    ))}
                                    {form.mode === 'create' && <option value="__new__">+ Créer un nouveau client tarifaire…</option>}
                                </select>
                            </div>
                            {form.mode === 'create' && form.pricingChoice === '__new__' && (
                                <div className="p-3 bg-slate-50 border rounded-lg space-y-2">
                                    <div>
                                        <Label className="text-xs">Nom du client tarifaire *</Label>
                                        <Input className={inputCls} value={form.newPricingName}
                                            onChange={e => set('newPricingName', e.target.value)}
                                            placeholder="ex. Leader Price" data-testid="new-pricing-name-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Mode de tarification</Label>
                                        <div className="flex gap-2 mt-1">
                                            <button type="button" onClick={() => set('tarifMode', 'direct')}
                                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tarifMode === 'direct' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600'}`}
                                                data-testid="mode-direct-btn">Tarifs fixes</button>
                                            <button type="button" onClick={() => set('tarifMode', 'commission')}
                                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tarifMode === 'commission' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600'}`}
                                                data-testid="mode-commission-btn">Avec commission %</button>
                                            {form.tarifMode === 'commission' && (
                                                <div className="flex items-center gap-1">
                                                    <Input className="h-8 w-20 text-sm" type="number" min="0" max="99" step="0.1"
                                                        value={form.commissionPct} onChange={e => set('commissionPct', e.target.value)}
                                                        placeholder="20" data-testid="commission-pct-input" />
                                                    <span className="text-xs text-slate-500">%</span>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-slate-400 mt-1">
                                            {form.tarifMode === 'commission'
                                                ? 'La commission sera déduite automatiquement dans les marges et le comparatif (comme Socoarmes).'
                                                : 'Les prix saisis dans Tarifs & marges seront pris tels quels (comme Soprorest ou Calidis).'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {form.mode === 'edit' && form.pricingChoice && form.pricingSaleType !== 'carrefour' && (
                                <div className="p-3 bg-slate-50 border rounded-lg">
                                    <Label className="text-xs">Mode de tarification du client tarifaire lié</Label>
                                    <div className="flex gap-2 mt-1">
                                        <button type="button" onClick={() => set('tarifMode', 'direct')}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tarifMode === 'direct' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600'}`}
                                            data-testid="edit-mode-direct-btn">Tarifs fixes</button>
                                        <button type="button" onClick={() => set('tarifMode', 'commission')}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tarifMode === 'commission' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600'}`}
                                            data-testid="edit-mode-commission-btn">Avec commission %</button>
                                        {form.tarifMode === 'commission' && (
                                            <div className="flex items-center gap-1">
                                                <Input className="h-8 w-20 text-sm" type="number" min="0" max="99" step="0.1"
                                                    value={form.commissionPct} onChange={e => set('commissionPct', e.target.value)}
                                                    data-testid="edit-commission-pct-input" />
                                                <span className="text-xs text-slate-500">%</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center gap-2 pt-1">
                                <input type="checkbox" id="client-hasbl" className="h-4 w-4 accent-teal-700"
                                    checked={!!form.has_bl} onChange={e => set('has_bl', e.target.checked)}
                                    data-testid="client-hasbl-checkbox" />
                                <Label htmlFor="client-hasbl" className="text-xs cursor-pointer">
                                    Ce client reçoit des bons de livraison (BL)
                                </Label>
                            </div>
                            {form.has_bl ? (
                                <div className="flex items-center gap-2 -mt-1">
                                    <input type="checkbox" id="client-blprices" className="h-4 w-4 accent-teal-700"
                                        checked={!!form.bl_with_prices} onChange={e => set('bl_with_prices', e.target.checked)}
                                        data-testid="client-blprices-checkbox" />
                                    <Label htmlFor="client-blprices" className="text-xs cursor-pointer">
                                        BL avec tarifs (Tarif HT par ligne + Total HT, TVA 2,10 %, Total TTC)
                                    </Label>
                                </div>
                            ) : (
                                <p className="text-[11px] text-slate-400 -mt-1">
                                    Aucun numéro de BL ne sera attribué : la ligne N° BL, l'onglet BL et les impressions BL seront masqués pour ce client.
                                </p>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                {form.has_bl && (
                                    <div>
                                        <Label className="text-xs">N° de départ des BL {form.mode === 'edit' && form.hasBl && <span className="text-slate-400">(BL déjà émis — sans effet)</span>}</Label>
                                        <Input className={inputCls} type="number" min="1" value={form.bl_start}
                                            disabled={form.mode === 'edit' && form.hasBl}
                                            onChange={e => set('bl_start', e.target.value)} data-testid="client-blstart-input" />
                                    </div>
                                )}
                                {form.mode === 'create' && (
                                    <div>
                                        <Label className="text-xs">Recopier les lignes de (optionnel)</Label>
                                        <select className={selCls} value={form.copy_products_from}
                                            onChange={e => set('copy_products_from', e.target.value)}
                                            data-testid="client-copyfrom-select">
                                            <option value="">— Démarrer vide —</option>
                                            {clients.map(c => <option key={c.id} value={c.id}>{c.label} ({(c.product_ids || []).length} lignes)</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <Button variant="outline" size="sm" onClick={() => setForm(null)} data-testid="client-form-cancel">Annuler</Button>
                                <Button size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={submit}
                                    disabled={saving || !form.label.trim() || (form.mode === 'create' && (!form.code || (form.pricingChoice === '__new__' && !form.newPricingName.trim())))}
                                    data-testid="client-form-submit">
                                    {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                                    {form.mode === 'create' ? 'Créer le client' : 'Enregistrer'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {clients.map(c => (
                                <div key={c.id} className={`flex items-center gap-3 p-2.5 border rounded-lg ${c.active ? 'bg-white' : 'bg-slate-50 opacity-70'}`}
                                    data-testid={`client-row-${c.code}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-sm">{c.label}</span>
                                            <span className="font-mono text-[10px] text-slate-400">{c.code}</span>
                                            {c.active
                                                ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Actif</Badge>
                                                : <Badge variant="outline" className="text-[10px] text-slate-500">Archivé</Badge>}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {c.point_de_vente || 'Sans point de vente'} · {c.pricing_client ? `${c.pricing_client.name} — ${modeLabel(c.pricing_client)}` : 'Aucun client tarifaire'}
                                            {' · '}{c.has_bl === false ? 'Sans BL' : c.last_bl ? `Dernier BL n° ${c.last_bl}` : `Prochain BL n° ${c.bl_start || 1}`}
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEdit(c)} title="Modifier" data-testid={`client-edit-${c.code}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                    {c.active ? (
                                        <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-600 hover:text-amber-700" onClick={() => setArchiveTarget(c)} title="Archiver" data-testid={`client-archive-${c.code}`}>
                                            <Archive className="w-3.5 h-3.5" />
                                        </Button>
                                    ) : (
                                        <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-600 hover:text-emerald-700" onClick={() => toggleActive(c, true)} title="Réactiver" data-testid={`client-restore-${c.code}`}>
                                            <ArchiveRestore className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button variant="outline" size="sm" className="w-full border-dashed" onClick={openCreate} data-testid="new-client-btn">
                                <Plus className="w-4 h-4 mr-1.5" /> Nouveau client
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
                <AlertDialogContent data-testid="archive-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archiver {archiveTarget?.label} ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Le client disparaîtra des pastilles, grilles, BL, facturation et impressions.
                            Tout son historique (quantités, numéros de BL, facturation) est <b>conservé</b> et
                            sera réaffiché si vous le réactivez.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => { toggleActive(archiveTarget, false); setArchiveTarget(null); }}
                            data-testid="archive-confirm-btn">
                            Archiver
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
